/**
 * <brick-order-tracker>: order lookup for sections/order-tracking.liquid.
 * Adapted from the parent theme (Shopify-only, no third-party services).
 *
 * Lookup key (shared contract with whatever writes the status records,
 * e.g. a future tools/brick-studio):
 *   handle = first 32 hex chars of SHA-256(`${orderDigits}|${emailLower}`)
 *   orderDigits = order number without non-digits ('#1042' -> '1042')
 *   emailLower  = trimmed, lower-cased email
 *
 * The handle is queried on the same-origin Storefront API
 * (POST /api/2025-10/graphql.json, public storefront token from the shop
 * metafield brick.storefront_token) as a `brick_order_status` metaobject.
 * Fields: status (ordered|packed|shipped|out_for_delivery|delivered),
 * first_name, order_number, headline, message, eta, carrier,
 * tracking_number, tracking_url, events (JSON [{label, note, at}]).
 * Only the first name is ever shown. A miss shows one privacy-safe
 * "not found" state (never says which field didn't match).
 * Also handles the "Have a tracking number?" carrier form (links built
 * from carrier URL templates set in the theme editor).
 */
(() => {
  const normalizeOrder = (value) => String(value || '').replace(/\D+/g, '');
  const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

  const orderHandle = async (order, email) => {
    const data = new TextEncoder().encode(`${normalizeOrder(order)}|${normalizeEmail(email)}`);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 32);
  };

  window.BrickTracking = Object.assign(window.BrickTracking || {}, { orderHandle, normalizeOrder, normalizeEmail });

  if (typeof customElements === 'undefined' || customElements.get('brick-order-tracker')) return;

  const STAGES = ['ordered', 'packed', 'shipped', 'out_for_delivery', 'delivered'];
  const QUERY =
    'query OrderStatus($h: String!) { metaobject(handle: { type: "brick_order_status", handle: $h }) { fields { key value } } }';
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const motionOff = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const safeUrl = (value) => {
    try {
      const url = new URL(String(value || ''), window.location.href);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : '';
    } catch (error) {
      return '';
    }
  };

  const parseJson = (value, fallback) => {
    if (value == null || value === '') return fallback;
    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  };

  // '2026-10-05' is a calendar date: build it locally so it never shifts a day.
  const parseDate = (value) => {
    const text = String(value || '').trim();
    const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
    if (ymd) return { date: new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])), dateOnly: true };
    if (!/^\d{4}-\d{2}-\d{2}T/.test(text)) return null;
    const date = new Date(text);
    return Number.isNaN(date.getTime()) ? null : { date, dateOnly: false };
  };

  class OrderTracker extends HTMLElement {
    connectedCallback() {
      this.lang = document.documentElement.lang || 'en';
      this.form = this.querySelector('[data-lookup-form]');
      this.submit = this.querySelector('[data-submit]');
      this.submitLabel = this.querySelector('[data-submit-label]');
      this.idleLabel = this.submitLabel ? this.submitLabel.textContent : '';
      this.results = this.querySelector('[data-results]');
      this.status = this.querySelector('[data-status]');
      this.carriers = parseJson((this.querySelector('[data-carriers]') || {}).textContent, []);

      if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));
      // Disabled in markup so the email never lands in a GET URL without JS.
      if (this.submit) this.submit.disabled = false;
      this.form?.addEventListener('input', (event) => this.clearError(event.target));

      const carrierForm = this.querySelector('[data-carrier-form]');
      if (carrierForm) carrierForm.addEventListener('submit', (event) => this.onCarrierSubmit(event, carrierForm));

      this.querySelector('[data-copy]')?.addEventListener('click', () => this.copyNumber());
      this.querySelector('[data-reset]')?.addEventListener('click', () => this.reset());
    }

    /* ---------- validation ---------- */
    fieldError(name, message) {
      const input = this.form.elements[name];
      const error = this.form.querySelector(`[data-error-for="${name}"]`);
      if (!input || !error) return;
      input.setAttribute('aria-invalid', 'true');
      error.textContent = message;
      error.hidden = false;
    }

    clearError(input) {
      if (!input || !input.name) return;
      input.removeAttribute('aria-invalid');
      const error = this.form.querySelector(`[data-error-for="${input.name}"]`);
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }
    }

    validate() {
      const order = this.form.elements.order;
      const email = this.form.elements.email;
      [order, email].forEach((input) => this.clearError(input));
      const digits = normalizeOrder(order.value);
      const invalid = [];
      if (!digits || digits.length > 12) {
        this.fieldError('order', this.dataset.textInvalidOrder);
        invalid.push(order);
      }
      if (!EMAIL.test(normalizeEmail(email.value))) {
        this.fieldError('email', this.dataset.textInvalidEmail);
        invalid.push(email);
      }
      if (invalid.length) {
        invalid[0].focus();
        return null;
      }
      return { digits, email: normalizeEmail(email.value) };
    }

    /* ---------- lookup ---------- */
    async onSubmit(event) {
      event.preventDefault();
      if (this.busy) return;
      const values = this.validate();
      if (!values) return;

      this.setBusy(true);
      this.showState('loading');
      const started = Date.now();
      let result;
      try {
        result = await this.lookup(values);
      } catch (error) {
        result = { state: 'error' };
      }
      // A short, calm loading beat so the state change is perceivable.
      const wait = motionOff() ? 0 : Math.max(0, 650 - (Date.now() - started));
      if (wait) await new Promise((resolve) => setTimeout(resolve, wait));
      this.setBusy(false);

      if (result.state === 'found') {
        this.renderFound(result.fields, values.digits);
        this.showState('found');
        this.animateProgress(result.fields.status);
      } else {
        this.showState(result.state);
      }
    }

    async lookup({ digits, email }) {
      const token = this.dataset.token;
      if (!token || !window.crypto || !crypto.subtle) return { state: 'error' };
      const handle = await orderHandle(digits, email);
      const response = await fetch(this.dataset.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query: QUERY, variables: { h: handle } }),
      });
      if (!response.ok) return { state: 'error' };
      const json = await response.json();
      const metaobject = json && json.data ? json.data.metaobject : null;
      if (!metaobject) return { state: json && json.errors && !json.data ? 'error' : 'not-found' };
      const fields = {};
      (metaobject.fields || []).forEach((field) => {
        fields[field.key] = field.value;
      });
      return { state: 'found', fields };
    }

    setBusy(busy) {
      this.busy = busy;
      if (this.submit) {
        this.submit.toggleAttribute('disabled', busy);
        if (busy) this.submit.setAttribute('aria-busy', 'true');
        else this.submit.removeAttribute('aria-busy');
      }
      if (this.submitLabel) this.submitLabel.textContent = busy ? this.dataset.textLoading : this.idleLabel;
      if (this.status) this.status.textContent = busy ? this.dataset.textLoading : '';
    }

    showState(state) {
      this.results.querySelectorAll('[data-state]').forEach((panel) => {
        panel.hidden = panel.dataset.state !== state;
      });
      if (state === 'loading') return;
      const panel = this.results.querySelector(`[data-state="${state}"]`);
      if (!panel) return;
      const target =
        state === 'found' ? panel.querySelector('[data-field="greeting"]') : panel.querySelector('[data-state-title]');
      panel.scrollIntoView({ behavior: motionOff() ? 'auto' : 'smooth', block: 'start' });
      if (target) target.focus({ preventScroll: true });
    }

    /* ---------- rendering (textContent only, no PII beyond first name) ---------- */
    setField(name, value) {
      const el = this.results.querySelector(`[data-field="${name}"]`);
      if (!el) return null;
      el.textContent = value || '';
      return el;
    }

    renderFound(fields, digits) {
      const status = STAGES.includes(fields.status) ? fields.status : 'ordered';
      const firstName = String(fields.first_name || '').trim().split(/\s+/)[0];
      this.setField(
        'greeting',
        firstName ? this.dataset.textGreeting.replace('[name]', firstName) : this.dataset.textGreetingFallback
      );
      const number = normalizeOrder(fields.order_number) || digits;
      this.setField('order', this.dataset.textOrder.replace('[number]', `#${number}`));

      const fallbackHeadline = this.dataset[`headline${status.replace(/(^|_)(\w)/g, (_, __, c) => c.toUpperCase())}`];
      this.setField('headline', fields.headline || fallbackHeadline);
      const message = this.setField('message', fields.message);
      if (message) message.hidden = !fields.message;

      const etaWrap = this.results.querySelector('[data-eta]');
      const eta = String(fields.eta || '').trim();
      if (etaWrap) {
        etaWrap.hidden = !eta || status === 'delivered';
        const parsed = parseDate(eta);
        this.setField(
          'eta',
          parsed
            ? new Intl.DateTimeFormat(this.lang, { weekday: 'long', day: 'numeric', month: 'long' }).format(parsed.date)
            : eta
        );
      }

      this.renderShipment(fields);
      this.renderEvents(parseJson(fields.events, []));
    }

    carrierUrl(fields) {
      const direct = safeUrl(fields.tracking_url);
      if (direct) return direct;
      const number = String(fields.tracking_number || '').trim();
      if (!number) return '';
      const carrier = String(fields.carrier || '').trim().toLowerCase();
      const match = carrier && this.carriers.find((entry) => String(entry.name || '').toLowerCase() === carrier);
      const template = (match && match.url) || '';
      if (!template.includes('{number}')) return '';
      return safeUrl(template.replace('{number}', encodeURIComponent(number)));
    }

    renderShipment(fields) {
      const wrap = this.results.querySelector('[data-shipment]');
      if (!wrap) return;
      const carrier = String(fields.carrier || '').trim();
      const number = String(fields.tracking_number || '').trim();
      const url = this.carrierUrl(fields);

      this.setField('carrier', carrier);
      this.setField('tracking_number', number);
      this.results.querySelector('[data-carrier-row]').hidden = !carrier;
      this.results.querySelector('[data-number-row]').hidden = !number;
      const link = this.results.querySelector('[data-carrier-link]');
      link.hidden = !url;
      if (url) link.href = url;
      wrap.hidden = !carrier && !number && !url;
    }

    renderEvents(events) {
      const wrap = this.results.querySelector('[data-events-wrap]');
      const list = this.results.querySelector('[data-events]');
      if (!wrap || !list) return;
      const items = (Array.isArray(events) ? events : [])
        .filter((event) => event && (event.label || event.note))
        .map((event, index) => ({ ...event, index, parsed: parseDate(event.at) }));
      // Newest first when dates are known; otherwise keep the given order reversed.
      items.sort((a, b) => {
        if (a.parsed && b.parsed) return b.parsed.date - a.parsed.date;
        return b.index - a.index;
      });

      const dateFormat = new Intl.DateTimeFormat(this.lang, { day: 'numeric', month: 'short' });
      const dateTimeFormat = new Intl.DateTimeFormat(this.lang, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });

      const nodes = items.map((event, position) => {
        const li = document.createElement('li');
        li.className = `tracker__event${position === 0 ? ' is-latest' : ''}`;
        const dot = document.createElement('span');
        dot.className = 'tracker__event-dot';
        dot.setAttribute('aria-hidden', 'true');
        const body = document.createElement('div');
        body.className = 'tracker__event-body';
        if (event.label) {
          const label = document.createElement('p');
          label.className = 'tracker__event-label';
          label.textContent = event.label;
          body.append(label);
        }
        if (event.note) {
          const note = document.createElement('p');
          note.className = 'tracker__event-note';
          note.textContent = event.note;
          body.append(note);
        }
        if (event.at) {
          const time = document.createElement('time');
          time.className = 'tracker__event-time';
          if (event.parsed) {
            time.dateTime = String(event.at);
            time.textContent = (event.parsed.dateOnly ? dateFormat : dateTimeFormat).format(event.parsed.date);
          } else {
            time.textContent = String(event.at);
          }
          body.append(time);
        }
        li.append(dot, body);
        return li;
      });
      list.replaceChildren(...nodes);
      wrap.hidden = nodes.length === 0;
    }

    animateProgress(rawStatus) {
      const wrap = this.results.querySelector('.stepper-wrap');
      if (!wrap) return;
      const index = Math.max(0, STAGES.indexOf(rawStatus));
      const doneText = (wrap.querySelector('[data-text-done]') || {}).textContent || '';
      const currentText = (wrap.querySelector('[data-text-current]') || {}).textContent || '';

      wrap.querySelectorAll('[data-step]').forEach((step, i) => {
        step.classList.toggle('is-done', i < index || (i === index && i === STAGES.length - 1));
        step.classList.toggle('is-current', i === index);
        if (i === index) step.setAttribute('aria-current', 'step');
        else step.removeAttribute('aria-current');
        const state = step.querySelector('[data-step-state]');
        if (state) state.textContent = i < index ? `, ${doneText}` : i === index ? `, ${currentText}` : '';
      });

      wrap.classList.toggle('is-delivered', index === STAGES.length - 1);
      const progress = index / (STAGES.length - 1);
      if (motionOff()) {
        wrap.style.setProperty('--progress', progress);
        return;
      }
      wrap.style.setProperty('--progress', 0);
      void wrap.offsetWidth;
      requestAnimationFrame(() => wrap.style.setProperty('--progress', progress));
    }

    async copyNumber() {
      const code = this.results.querySelector('[data-field="tracking_number"]');
      const label = this.results.querySelector('[data-copy-label]');
      const button = this.results.querySelector('[data-copy]');
      if (!code || !code.textContent) return;
      try {
        await navigator.clipboard.writeText(code.textContent);
      } catch (error) {
        const range = document.createRange();
        range.selectNodeContents(code);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        return;
      }
      if (label) label.textContent = this.dataset.textCopied;
      button.dataset.state = 'copied';
      if (this.status) this.status.textContent = this.dataset.textCopied;
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => {
        if (label) label.textContent = this.dataset.textCopy;
        delete button.dataset.state;
      }, 2000);
    }

    reset() {
      this.results.querySelectorAll('[data-state]').forEach((panel) => {
        panel.hidden = true;
      });
      this.form.reset();
      const order = this.form.elements.order;
      order.scrollIntoView({ behavior: motionOff() ? 'auto' : 'smooth', block: 'center' });
      order.focus({ preventScroll: true });
    }

    /* ---------- carrier quick form ---------- */
    onCarrierSubmit(event, form) {
      event.preventDefault();
      const error = form.querySelector('[data-carrier-error]');
      const input = form.querySelector('[data-number-input]');
      const select = form.querySelector('[data-carrier-select]');
      const number = String(input.value || '').replace(/\s+/g, '').trim();
      error.hidden = true;
      input.removeAttribute('aria-invalid');
      if (!number) {
        input.setAttribute('aria-invalid', 'true');
        input.focus();
        return;
      }
      const template = select ? select.value : '';
      const url = template.includes('{number}') ? safeUrl(template.replace('{number}', encodeURIComponent(number))) : '';
      if (!url) {
        error.textContent = this.dataset.textTemplateError;
        error.hidden = false;
        return;
      }
      window.open(url, '_blank', 'noopener');
    }
  }

  customElements.define('brick-order-tracker', OrderTracker);
})();
